import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowDownRight, MessageCircle, Star, MapPin, BookOpen } from "lucide-react";
import { WA } from "./data";
import { ease, scrollToId } from "./motion";

const LINES = [["Água quente,"], ["pressão certa,", "accent"], ["sol da Bahia."]];

const Sun = ({ y }) => (
  <motion.svg style={{ y }} viewBox="0 0 400 400" className="absolute -right-24 -top-24 md:right-[-6%] md:top-[-10%] w-[340px] md:w-[620px] opacity-90 pointer-events-none" aria-hidden="true">
    <defs><radialGradient id="sg" cx="50%" cy="50%" r="50%"><stop offset="0%" stopColor="#FFB020" /><stop offset="60%" stopColor="#FF6A13" /><stop offset="100%" stopColor="#FF6A13" stopOpacity="0" /></radialGradient></defs>
    <circle cx="200" cy="200" r="190" fill="url(#sg)" opacity="0.35" />
    <motion.g animate={{ rotate: 360 }} transition={{ duration: 80, repeat: Infinity, ease: "linear" }} style={{ originX: "200px", originY: "200px" }}>
      {[...Array(36)].map((_, i) => (
        <line key={i} x1="200" y1="40" x2="200" y2={i % 2 ? "62" : "76"} stroke="#FFB020" strokeWidth="3" strokeLinecap="round" transform={`rotate(${i * 10} 200 200)`} />
      ))}
    </motion.g>
    <circle cx="200" cy="200" r="98" fill="#FF6A13" />
    <circle cx="200" cy="200" r="98" fill="none" stroke="#FFB020" strokeWidth="2" />
  </motion.svg>
);

const TiltPhoto = () => {
  const mx = useMotionValue(0), my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [9, -9]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-11, 11]), { stiffness: 120, damping: 14 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <div className="relative [perspective:1200px]" onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }} data-testid="hero-storefront-card">
    <motion.div
      initial={{ opacity: 0, clipPath: "inset(100% 0 0 0)" }} animate={{ opacity: 1, clipPath: "inset(0% 0 0 0)" }}
      transition={{ duration: 1.4, ease, delay: 0.9 }}
    >
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative rounded-[28px] overflow-hidden ring-1 ring-white/15 shadow-[0_40px_120px_-20px_rgba(255,106,19,0.35)]">
        <img src="/img/fachada.jpg" alt="Fachada da loja RSilva Aquecimento Solar e Bombas em Lauro de Freitas" className="w-full h-[380px] sm:h-[460px] lg:h-[540px] object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E3F] via-transparent to-transparent" />
        <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-3" style={{ transform: "translateZ(50px)" }}>
          <div className="text-white">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/75">Loja 03 · Centro</p>
            <p className="font-display text-lg font-bold mt-1">Av. Santos Dumont, 1926</p>
          </div>
          <MapPin className="w-6 h-6 text-[#FFB020] shrink-0" />
        </div>
      </motion.div>
    </motion.div>
      <motion.div
        initial={{ scale: 0, rotate: -20 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 180, damping: 14, delay: 1.8 }}
        className="absolute left-3 sm:-left-10 top-8 z-10 bg-[#F4EFE6] text-[#0A0E3F] rounded-2xl px-4 py-3 shadow-2xl" data-testid="hero-rating-badge"
      >
        <div className="flex items-center gap-1">{[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-[#FF6A13] text-[#FF6A13]" />)}</div>
        <p className="font-display font-black text-3xl leading-none mt-1.5">4,8</p>
        <p className="text-xs mt-1 text-[#0A0E3F]/75">19 avaliações no Google</p>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const sunY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  return (
    <section id="topo" ref={ref} className="relative min-h-[100svh] bg-[#0A0E3F] overflow-hidden grain" data-testid="hero-section">
      <Sun y={sunY} />
      <div className="absolute inset-0 grid-lines pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 pt-32 md:pt-40 pb-20 grid lg:grid-cols-12 gap-14 lg:gap-10 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }}
            className="font-mono text-xs uppercase tracking-[0.25em] text-[#FFB020] mb-8">Aquecimento Solar e Bombas · Lauro de Freitas</motion.p>
          <h1 className="font-display font-black text-white text-[13vw] sm:text-6xl lg:text-[5.6rem] xl:text-[6.4rem] leading-[0.92] tracking-[-0.04em]" data-testid="hero-title">
            {LINES.map(([t, v], i) => (
              <span key={t} className="block overflow-hidden pb-[0.08em]">
                <motion.span className={`block ${v ? "italic text-[#FF6A13]" : ""}`}
                  initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, ease, delay: 0.35 + i * 0.14 }}>
                  {t}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.9, ease }}
            className="mt-8 max-w-xl text-base md:text-lg text-white/80 leading-relaxed">
            A RSilva é a sua loja de aquecimento solar, aquecedores a gás, bombas, piscinas, tubos e conexões. Atendimento que resolve, produtos das melhores marcas e entrega.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.9, ease }} className="mt-10 flex flex-wrap gap-3">
            <a href={WA} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn"
              className="group inline-flex items-center gap-3 rounded-full bg-[#FF6A13] text-[#0A0E3F] font-semibold pl-6 pr-2 py-2 hover:bg-[#FFB020] transition-colors duration-300">
              Pedir orçamento
              <span className="grid place-items-center w-10 h-10 rounded-full bg-[#0A0E3F] text-white transition-transform duration-500 group-hover:rotate-[-20deg] group-hover:scale-110"><MessageCircle className="w-4 h-4" /></span>
            </a>
            <a href="https://canva.link/catalogo-rsilva" target="_blank" rel="noopener noreferrer" data-testid="hero-catalog-btn"
              className="group inline-flex items-center gap-3 rounded-full bg-[#F4EFE6] text-[#0A0E3F] font-semibold pl-6 pr-2 py-2 hover:bg-white transition-colors duration-300">
              Ver catálogo
              <span className="grid place-items-center w-10 h-10 rounded-full bg-[#FF6A13] text-[#0A0E3F] transition-transform duration-500 group-hover:rotate-[-20deg] group-hover:scale-110"><BookOpen className="w-4 h-4" /></span>
            </a>
            <a href="#produtos" onClick={(e) => scrollToId(e, "produtos")} data-testid="hero-products-btn"
              className="group inline-flex items-center gap-2 rounded-full border border-white/25 text-white px-6 py-4 hover:border-white hover:bg-white/5 transition-colors duration-300">
              Ver produtos <ArrowDownRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
            </a>
          </motion.div>
        </motion.div>
        <div className="lg:col-span-5 lg:pl-6"><TiltPhoto /></div>
      </div>
    </section>
  );
};
